import { defineStore } from 'pinia';
import { useForm } from '@inertiajs/vue3';

import { Notify, Dialog } from 'quasar';

import axios from 'axios';

axios.defaults.baseURL = import.meta.env.AXIOS_BASE_URL;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

export const useWorkOrderDialogStore = defineStore('WorkOrderDialogStore', {
    state: () => ({
        open: false,
        loaded: false,
        locked: false,
        docked: false,
        editing: false,
        minimized: false,
        loading: {
            workOrder: false,
            workItems: false,
            customer: false,
        },
        customer: {
            id: null,
            contact_id: null,
            created_at: null,
            updated_at: null,
            contact: {
                id: null,
                org_name: null,
            },
        },
        workOrder: useForm({
            id: null,
            customer_id: null,
            completed_at: null,
            created_at: null,
            updated_at: null,
            contact_id: null,
            work_start_at: null,
            invoice_id: null,
            invoice: null,
            contact: null,
            customer: {
                id: null,
                contact_id: null,
                created_at: null,
                updated_at: null,
                contact: {
                    id: null,
                    org_name: null,
                },
            },
        }),
        workItems: {
            rows: [],
            editing: [],

            filter: {
                form: useForm({
                    page: 1,
                    rows: 25,
                    search: '',
                    sort: 'created_at',
                    direction: 'desc',
                    searchColumn: [],
                }),
                to: 0,
                from: 0,
                total: 0,
                last_page: 1,
                current_page: 1,
            }
        },
    }),
    actions: {
        async openDialog(workOrderId = null, customerId = null) {
            // If a work order is loaded and a new one is 
            // selected, fetch the new one
            if (workOrderId && this.workOrder.id !== workOrderId) {
                this.loaded = false;
                // Clear out existing data
                this.reset();
            }

            if (workOrderId && !this.loaded) {
                await this.fetch(workOrderId, customerId);
            }
            
            if (!this.open) {
                this.open = true;
            } else {
                this.minimized = false;
                this.docked = false;
            }
        },
        async closeDialog() {
            this.open = false;

            this.reset();
        },
        async fetch(workOrderId = null, customerId = null, forceRefresh = false) {
            if (this.loaded && !forceRefresh) return;

            await this.getWorkOrder(workOrderId ?? this.workOrder.id, forceRefresh);
            await this.getWorkItems(workOrderId ?? this.workOrder.id, forceRefresh);
            await this.getCustomer(customerId ?? this.workOrder.customer_id, forceRefresh);

            this.loaded = true;
        },
        async getWorkOrder(workOrderId, forceRefresh = false) {
            if (this.loading.workOrder && !forceRefresh) return;
                
            this.loading.workOrder = true;

            try {
                const { data } = await axios.get(
                    route('api.v1.work-orders.show', workOrderId)
                );

                if (!data.success) {
                    console.error(data.message ?? 'An error occurred while fetching the work order.');
                    this.loading = false;
                    return;
                }
                
                this.workOrder = useForm(data.work_order);
            } catch (error) {
                console.error(error);
            }
        },
        async getWorkItems(workOrderId = null, forceRefresh = false) {
            if (this.loading.workItems && !forceRefresh) return;

            if ((workOrderId ?? this.workOrder.id) === null) {
                console.error('No work order ID provided.');
                return;
            }

            this.loading.workItems = true;

            try {
                const { data } = await axios.get(
                    route('api.v1.work-orders.items', workOrderId ?? this.workOrder.id), {
                        params: this.workItems.filter.form.data(),
                    }
                );

                this.workItems.rows = data.data;

                this.workItems.filter.to = data.to;
                this.workItems.filter.from = data.from;
                this.workItems.filter.total = data.total;
                this.workItems.filter.last_page = data.last_page;
                this.workItems.filter.current_page = data.current_page;
            } catch (error) {
                console.error(error ?? 'An error occurred while fetching the work items.');
            } finally {
                this.loading.workItems = false;
            }
        },
        async getCustomer(customerId = null, forceRefresh = false) {
            if (this.loading.customer && !forceRefresh) return;
            if ((customerId ?? this.workOrder.customer_id) === null) {
                console.error('No customer ID provided.');
                return;
            }

            this.loading.customer = true;

            try {
                const { data } = await axios.get(
                    route('api.v1.customer.work-types', customerId ?? this.workOrder.customerId)
                );

                if (!data.success) {
                    console.error(data.message ?? 'An error occurred while fetching the customer.');
                    this.loading = false;
                    return;
                }

                this.customer = useForm({
                    id: customerId ?? this.workOrder.customer_id,
                    ...data.data
                });
            } catch (error) {
                console.error(error ?? 'An error occurred while fetching the customer.');
            } finally {
                this.loading.customer = false;
            }
        },
        reset() {
            this.loaded = false;
            this.loading.workOrder = false;
            this.loading.workItems = false;
            this.loading.customer = false;
            
            this.workOrder.reset();
            this.workItems.rows = [];
            this.workItems.editing = [];

            this.workItems.filter.form.reset();

            this.workItems.filter.to = 0;
            this.workItems.filter.from = 0;
            this.workItems.filter.total = 0;
            this.workItems.filter.last_page = 1;
            this.workItems.filter.current_page = 1;

            this.customer = {
                id: null,
                contact_id: null,
                created_at: null,
                updated_at: null,
                contact: {
                    id: null,
                    org_name: null,
                },
            };
        },
        create() {
            // TODO: Should get the last work item and use that as a template for the new work item
            this.workItems.editing.push(
                useForm({
                    id: null,
                    work_type_id: null,
                    work_order_id: this.workOrder.id,
                    sub_customer_id: null,
                    notes: null,
                    identifiers: [],
                    values: [],
                    pay: [],
                    // These are set when the work item is created
                    created_at: null,
                    updated_at: null,
                    billing_pay: 0,
                    total_lumper_pay: 0, // This should be worker pay
                    total_labor_split: 0,
                    // The following will be set when things are selected
                    sub_customer: null,
                    work_type: null,
                    customer: null,
                })
            );
        },
        update() {
            // 
        },
        destroy() {
            // 
        },
        restore() {
            // 
        },
        forceDelete() {
            //
        },
    },
    getters: {
        isDirty() {
            return false;
            // if (!this.loaded) return false;

            // return this.payCodes.some((payCode) => payCode.isDirty);
        },
        isLoaded() {
            return this.loaded;
        }
    }
});